import React from "react";
import { Stack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { CalendarServiceEvent, Meeting, MeetingId } from "features/Resources/Meeting/models";
import { Text14 } from "components/Text";
import { NoMeetingsBeachDay } from "components/EmptyComponents/RandomEmptyState";
import { dayDifferenceLabel, meetingDay, meetingStartEnd } from "helpers/time";
import { DisplayAttendeesColumn } from "features/Resources/Meeting/Components/DisplayAttendees";
import SelectSessionButton from "features/Resources/Meeting/Components/Button/SelectSessionButton";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { useAvailableSessionsQuery, useMeetingDetailQuery } from "features/Resources/Meeting/hooks";
import { NewHireJourney } from "models/v2/newHireJourney";

const columnHelper = createColumnHelper<CalendarServiceEvent>();

function EventDayTime({ event, newHireJourney, isLoading }: {
  isLoading?: boolean;
  event: CalendarServiceEvent;
  newHireJourney: NewHireJourney | undefined;
}) {
  if (!event.startTime || isLoading || !newHireJourney) {
    return "Loading...";
  }
  return (
    <Stack gap="2px">
      <Text14 fontWeight="700">{`${meetingDay(event.startTime)} (${dayDifferenceLabel(newHireJourney?.startDate, event.startTime)})`}</Text14>
      <Text14>{meetingStartEnd(event.startTime, event.endTime, newHireJourney?.newHire.timezone)}</Text14>
    </Stack>
  );
}

function buildColumns(
  meeting: Meeting | undefined,
  newHireJourney: NewHireJourney | undefined,
  isLoading: boolean,
  renderAction?: (event: CalendarServiceEvent) => React.ReactNode,
) {
  return [
    columnHelper.accessor((row) => row, {
      id: "scheduled",
      header: "When",
      size: "200px",
      minSize: "200px",
      maxSize: "300px",
      cell: (row) => <EventDayTime event={row.getValue()} newHireJourney={newHireJourney} isLoading={isLoading} />,
    }),
    columnHelper.accessor((row) => row, {
      id: "attendees",
      header: "Guests",
      size: "250px",
      minSize: "250px",
      maxSize: "250px",
      cell: (row) => <DisplayAttendeesColumn attendees={row.getValue().attendees} />,
    }),
    columnHelper.accessor((row) => row, {
      id: "buttons",
      header: "",
      size: "150px",
      minSize: "150px",
      maxSize: "150px",
      cell: (row) => {
        const event = row.getValue();
        if (renderAction) return renderAction(event);
        return <SelectSessionButton meeting={meeting} eventId={event?.id} />;
      },
    }),
  ];
}

interface SelectFromMeetingListProps {
  meetingId: MeetingId;
  renderAction?: (event: CalendarServiceEvent) => React.ReactNode;
}

export default function SelectFromMeetingList({ meetingId, renderAction }: SelectFromMeetingListProps) {
  const { data: sessions, isPending: sessionsPending } = useAvailableSessionsQuery(meetingId);
  const { data: meeting, isPending: meetingPending } = useMeetingDetailQuery(meetingId);
  const { data: newHireJourney, isPending: newHirePending } = useNewHireJourneyDetailQuery(meeting?.newHireJourneyId);

  const isLoading = sessionsPending || meetingPending || newHirePending;

  return (
    <Stack direction="column" gap="4" w="100%">
      <ControlledDataTable<CalendarServiceEvent>
        data={sessions || []}
        isLoading={isLoading}
        columns={buildColumns(meeting, newHireJourney, isLoading, renderAction)}
        emptyStateComponent={<NoMeetingsBeachDay message="No existing sessions found" />}
        useFixedColumns
        sx={{
          "td, th": {
            py: 2,
            px: 3,
          },
        }}
      />
    </Stack>
  );
}
